import * as T from "../utilities/frontendTypes";
import { queryKeys } from "../utilities/queryKeys";
import { useMutationWithUI } from "../utilities/useMutationWithUI";
import {
  expertApproveSow,
  sendSowFeedback,
  submitFounderAssessment,
  saveFounderAssessment,
  saveFounderFullAssessment,
  addDDFlag,
  addNewNote,
  updateCalendlyLink,
  addConflict,
  sendIpeInvite,
  setBoardReview,
  removeBoardReview,
  addIpeMeeting,
  saveIpeAssessment,
  saveIpeRejection,
} from "./ipExpertService";
import textData from "../textData.json";

export function useExpertApproveSow() {
  return useMutationWithUI({
    mutationFn: (sowId: string) => expertApproveSow(sowId),
    invalidateKeys: [queryKeys.ipExpert.all, queryKeys.members.all, queryKeys.dashboards.all],
    successMessage: textData.Toasts.Expert.SowApproved,
  });
}

export function useSendSowFeedbackExpert() {
  return useMutationWithUI({
    mutationFn: (args: { sowFeedback: string; sowId: string }) =>
      sendSowFeedback(args.sowFeedback, args.sowId),
    invalidateKeys: [queryKeys.ipExpert.all, queryKeys.members.all, queryKeys.dashboards.all],
    successMessage: textData.Toasts.Expert.SowFeedbackRequested,
  });
}

export function useSubmitFounderAssessment() {
  return useMutationWithUI({
    mutationFn: (args: { id: T.UserId; assessment: T.ScoreBreakdown; duration: number }) =>
      submitFounderAssessment(args.id, args.assessment, args.duration),
    invalidateKeys: [queryKeys.ipExpert.all, queryKeys.members.all, queryKeys.dashboards.expert()],
    successMessage: textData.Toasts.Expert.AssessmentSubmitted,
  });
}

export function useSaveFounderAssessment() {
  return useMutationWithUI({
    mutationFn: (args: { id: T.UserId; duration: number; tierRec?: string }) =>
      saveFounderAssessment(args.id, args.duration, args.tierRec),
    invalidateKeys: [queryKeys.ipExpert.all, queryKeys.dashboards.expert()],
    showLoadingOverlay: false,
  });
}

export function useSaveFounderFullAssessment() {
  return useMutationWithUI({
    mutationFn: (args: { id: T.UserId; assessment: T.ScoreBreakdown; duration: number }) =>
      saveFounderFullAssessment(args.id, args.assessment, args.duration),
    invalidateKeys: [queryKeys.ipExpert.all, queryKeys.dashboards.expert()],
    showLoadingOverlay: false,
  });
}

export function useAddDDFlag() {
  return useMutationWithUI({
    mutationFn: (args: { id: T.UserId; notes: string }) =>
      addDDFlag(args.id, args.notes),
    invalidateKeys: [queryKeys.ipExpert.all, queryKeys.members.all, queryKeys.dashboards.expert()],
    successMessage: textData.Toasts.Expert.DdFlagAdded,
  });
}

export function useAddExpertNote() {
  return useMutationWithUI({
    mutationFn: (args: { id: T.ApplicationId; notes: string }) =>
      addNewNote(args.id, args.notes),
    invalidateKeys: [queryKeys.notes.all],
    successMessage: textData.Toasts.Expert.NoteAdded,
  });
}

export function useUpdateCalendlyLink() {
  return useMutationWithUI({
    mutationFn: (newLink: string) => updateCalendlyLink(newLink),
    invalidateKeys: [queryKeys.dashboards.expert()],
    successMessage: textData.Toasts.Expert.MeetingLinkUpdated,
  });
}

export function useAddConflict() {
  return useMutationWithUI({
    mutationFn: (args: { id: T.ApplicationId; notes: string }) =>
      addConflict(args.id, args.notes),
    invalidateKeys: [queryKeys.ipExpert.all, queryKeys.members.all, queryKeys.dashboards.expert()],
    successMessage: textData.Toasts.Expert.CoiFlagged,
  });
}

export function useSendIpeInvite() {
  return useMutationWithUI({
    mutationFn: (id: T.ApplicationId) => sendIpeInvite(id),
    invalidateKeys: [queryKeys.ipExpert.all, queryKeys.dashboards.expert()],
    successMessage: textData.Toasts.Expert.MeetingInviteSent,
  });
}

export function useSetBoardReview() {
  return useMutationWithUI({
    mutationFn: (id: T.ApplicationId) => setBoardReview(id),
    invalidateKeys: [queryKeys.ipExpert.all, queryKeys.dashboards.expert()],
    successMessage: textData.Toasts.Expert.MarkedForBoardReview,
  });
}

export function useRemoveBoardReview() {
  return useMutationWithUI({
    mutationFn: (id: T.ApplicationId) => removeBoardReview(id),
    invalidateKeys: [queryKeys.ipExpert.all, queryKeys.dashboards.expert()],
    successMessage: textData.Toasts.Expert.BoardReviewRemoved,
  });
}

export function useAddIpeMeeting() {
  return useMutationWithUI({
    mutationFn: (args: { id: T.ApplicationId; meetingType: string; duration: number; date: Date; notes: string }) =>
      addIpeMeeting(args.id, args.meetingType, args.duration, args.date, args.notes),
    invalidateKeys: [queryKeys.ipExpert.all, queryKeys.dashboards.expert()],
    successMessage: textData.Toasts.Expert.MeetingLogged,
  });
}

export function useSaveIpeAssessment() {
  return useMutationWithUI({
    mutationFn: (args: { id: T.ApplicationId; ipAssessmentObj: T.IpAssessmentObj; complete: boolean }) =>
      saveIpeAssessment(args.id, args.ipAssessmentObj, args.complete),
    invalidateKeys: [queryKeys.ipExpert.all, queryKeys.dashboards.expert()],
    showLoadingOverlay: false,
  });
}

export function useSaveIpeRejection() {
  return useMutationWithUI({
    mutationFn: (args: { id: T.ApplicationId; ipRejectionObj: T.IpRejectionObj; complete: boolean }) =>
      saveIpeRejection(args.id, args.ipRejectionObj, args.complete),
    invalidateKeys: [queryKeys.ipExpert.all, queryKeys.dashboards.expert()],
    showLoadingOverlay: false,
  });
}
